/**
 * Configuraciones centralizadas de menús para el sistema CAPIVIO
 * 
 * Este archivo contiene las definiciones de secciones y items para los diferentes
 * tipos de menús de administración, permitiendo fácil mantenimiento y personalización.
 * 
 * ESTRUCTURA PROFESIONAL:
 * - Menú de Usuario Normal: Solo funcionalidades básicas
 * - Admin de Negocio: Gestión de usuarios, oportunidades, reportes
 * - Admin de Sistema: Serialización, auditoría, configuración técnica
 */

import {
    Dashboard,
    People,
    Business,
    Settings,
    Security,
    Assessment,
    Notifications,
    AccountBalance,
    VerifiedUser,
    Analytics,
    Storage,
    Backup,
    BugReport,
    Code,
    Timeline,
    CheckCircle,
    MonetizationOn,
    TrendingUp,
    Description,
    Build,
    Speed,
    Restore,
    Message,
    WorkOutline,
    PieChart
} from "@mui/icons-material";

/**
 * Configuración del menú lateral para usuarios normales (no admin)
 * SOLO funcionalidades básicas de inversión y cuenta personal
 */
export const userMenuConfig = [
    {
        id: "dashboard",
        title: "Dashboard",
        icon: <Dashboard />,
        path: "/dashboard"
    },
    {
        id: "opportunities",
        title: "Oportunidades",
        icon: <WorkOutline />,
        path: "/opportunities"
    },
    {
        id: "portfolio",
        title: "Portafolio",
        icon: <PieChart />,
        path: "/portfolio"
    },
    {
        id: "investments",
        title: "Mis Inversiones",
        icon: <AccountBalance />,
        path: "/investments"
    },
    {
        divider: true
    },
    {
        id: "account",
        title: "Mi Cuenta",
        icon: <Settings />,
        items: [
            { id: "kyc", title: "Verificación KYC", icon: <VerifiedUser />, path: "/kyc" },
            { id: "profile", title: "Perfil", icon: <Settings />, path: "/profile" }
            // Elemento "Cerrar Cuenta" oculto
        ]
    },
    {
        id: "support",
        title: "Soporte",
        icon: <Message />,
        items: [
            { id: "messages", title: "Mensajes", icon: <Message />, path: "/messages" },
            { id: "notifications", title: "Notificaciones", icon: <Notifications />, path: "/notifications" }
        ]
    }
];

/**
 * Configuración del menú de Admin de Negocio
 * SOLO funcionalidades de gestión de negocio
 */
export const businessAdminMenuConfig = [
    {
        id: "dashboard",
        title: "Dashboard de Negocio",
        icon: <Dashboard />,
        path: "/admin/business"
    },
    {
        id: "userManagement",
        title: "Gestión de Usuarios",
        icon: <People />,
        items: [
            {
                id: "users",
                title: "Usuarios",
                icon: <People />,
                path: "/admin/business/users"
            },
            {
                id: "agents",
                title: "Agentes",
                icon: <Business />,
                path: "/admin/business/agents"
            },
            {
                id: "kycPending",
                title: "KYC Pendientes",
                icon: <VerifiedUser />,
                path: "/admin/business/kyc-pending"
            }
        ]
    },
    {
        id: "opportunities",
        title: "Oportunidades",
        icon: <Business />,
        items: [
            {
                id: "opportunityManagement",
                title: "Gestión de Oportunidades",
                icon: <Business />,
                path: "/admin/business/opportunities"
            },
            {
                id: "projectValidation",
                title: "Validación de Proyectos",
                icon: <CheckCircle />,
                path: "/admin/business/project-validation"
            },
            {
                id: "projectReview",
                title: "Revisión de Proyectos",
                icon: <Assessment />,
                path: "/admin/business/project-review"
            }
        ]
    },
    {
        id: "financial",
        title: "Financiero",
        icon: <AccountBalance />,
        items: [
            {
                id: "capitalManagement",
                title: "Gestión de Capital",
                icon: <AccountBalance />,
                path: "/admin/business/capital-management"
            },
            {
                id: "distributions",
                title: "Distribuciones",
                icon: <TrendingUp />,
                path: "/admin/business/distributions"
            },
            {
                id: "commissions",
                title: "Comisiones",
                icon: <MonetizationOn />,
                path: "/admin/business/commissions"
            }
        ]
    },
    {
        id: "reports",
        title: "Reportes",
        icon: <Assessment />,
        items: [
            {
                id: "investmentReports",
                title: "Reportes de Inversión",
                icon: <Assessment />,
                path: "/admin/business/investment-reports"
            },
            {
                id: "agentReports",
                title: "Reportes de Agentes",
                icon: <Business />,
                path: "/admin/business/agent-reports"
            },
            {
                id: "complianceReports",
                title: "Reportes de Compliance",
                icon: <Security />,
                path: "/admin/business/compliance-reports"
            }
        ]
    },
    {
        id: "compliance",
        title: "Compliance & Legal",
        icon: <Security />,
        items: [
            {
                id: "userAudit",
                title: "Auditoría de Usuarios",
                icon: <People />,
                path: "/admin/business/user-audit"
            },
            {
                id: "legalDocuments",
                title: "Documentos Legales",
                icon: <Description />,
                path: "/admin/business/legal-documents"
            }
        ]
    },
    {
        id: "logs",
        title: "Logs de Negocio",
        icon: <Description />,
        path: "/admin/business/logs"
    }
    // Elemento "Offboarding" oculto
];

/**
 * Configuración del menú de Admin de Sistema
 * SOLO funcionalidades técnicas y de infraestructura
 */
export const systemAdminMenuConfig = [
    {
        id: "dashboard",
        title: "Dashboard de Sistema",
        icon: <Dashboard />,
        path: "/admin/system"
    },
    {
        id: "serialization",
        title: "Serialización",
        icon: <Storage />,
        items: [
            {
                id: "serializationDashboard",
                title: "Dashboard de Serialización",
                icon: <Dashboard />,
                path: "/admin/system/serialization"
            },
            {
                id: "entityTraceability",
                title: "Trazabilidad de Entidades",
                icon: <Timeline />,
                path: "/admin/system/traceability"
            },
            {
                id: "migrationManagement",
                title: "Gestión de Migraciones",
                icon: <Backup />,
                path: "/admin/system/migrations"
            }
        ]
    },
    {
        id: "monitoring",
        title: "Monitoreo",
        icon: <Analytics />,
        items: [
            {
                id: "systemLogs",
                title: "Logs del Sistema",
                icon: <Description />,
                path: "/admin/system/logs"
            },
            {
                id: "performanceMonitoring",
                title: "Rendimiento",
                icon: <Speed />,
                path: "/admin/system/monitoring/performance"
            },
            {
                id: "errorTracking",
                title: "Seguimiento de Errores",
                icon: <BugReport />,
                path: "/admin/system/monitoring/errors"
            }
        ]
    },
    {
        id: "development",
        title: "Desarrollo",
        icon: <Code />,
        items: [
            {
                id: "devTools",
                title: "Herramientas de Desarrollo",
                icon: <Build />,
                path: "/admin/system/dev-tools"
            },
            {
                id: "apiManagement",
                title: "Gestión de APIs",
                icon: <Code />,
                path: "/admin/system/api"
            }
        ]
    },
    {
        id: "backup",
        title: "Backup y Recuperación",
        icon: <Backup />,
        items: [
            {
                id: "backupManagement",
                title: "Gestión de Backups",
                icon: <Backup />,
                path: "/admin/system/backup"
            },
            {
                id: "recoveryTools",
                title: "Herramientas de Recuperación",
                icon: <Restore />,
                path: "/admin/system/backup/recovery"
            }
        ]
    },
    {
        divider: true
    },
    {
        id: "configuration",
        title: "Configuración del Sistema",
        icon: <Settings />,
        path: "/admin/system/settings"
    }
];

/**
 * Colores por defecto para el menú de usuario normal
 */
export const userMenuDefaultColors = {
    background: "#1a237e",
    text: "#ffffff",
    activeItem: "#3949ab",
    hoverItem: "#283593",
    divider: "#e0e0e0"
};

/**
 * Colores por defecto para el menú de Admin de Negocio
 */
export const businessAdminDefaultColors = {
    background: "#1a237e",
    text: "#ffffff",
    activeItem: "#3949ab",
    hoverItem: "#283593",
    divider: "#e0e0e0"
};

/**
 * Colores por defecto para el menú de Admin de Sistema
 */
export const systemAdminDefaultColors = {
    background: "#2e7d32",
    text: "#ffffff",
    activeItem: "#4caf50",
    hoverItem: "#388e3c",
    divider: "#e0e0e0"
};

/**
 * Función helper para obtener la configuración de menú según el tipo
 * @param {string} menuType - Tipo de menú ("user", "business", "system")
 * @returns {Object} Configuración del menú
 */
export const getMenuConfig = (menuType) => {
    switch (menuType) {
    case "business":
        return {
            sections: businessAdminMenuConfig,
            defaultColors: businessAdminDefaultColors,
            title: "CAPIVIO Admin Negocio"
        };
    case "system":
        return {
            sections: systemAdminMenuConfig,
            defaultColors: systemAdminDefaultColors,
            title: "CAPIVIO Admin Sistema"
        };
    case "user":
    default:
        return {
            sections: userMenuConfig,
            defaultColors: userMenuDefaultColors,
            title: "CAPIVIO"
        };
    }
};

/**
 * Función helper para obtener colores personalizados desde localStorage
 * @returns {Object|null} Colores personalizados o null si no existen
 */
export const getCustomColors = () => {
    try {
        const savedColors = localStorage.getItem("sidebarColors");
        return savedColors ? JSON.parse(savedColors) : null;
    } catch (error) {
        return null;
    }
};

/**
 * Función helper para guardar colores personalizados
 * @param {Object} colors - Objeto con los colores a guardar
 */
export const saveCustomColors = (colors) => {
    try {
        localStorage.setItem("sidebarColors", JSON.stringify(colors));
    } catch (error) {
        // Error handling silencioso
    }
};

/**
 * Función helper para limpiar colores personalizados
 */
export const clearCustomColors = () => {
    try {
        localStorage.removeItem("sidebarColors");
    } catch (error) {
        // Error handling silencioso
    }
}; 